/**
 * DEVICE-LOCAL per-row change tracking — the durable facts both sync engines
 * derive their dirty sets from.
 *
 * `mut` is bumped by the repository chokepoint on every local write of synced
 * content (content updates, soft deletes, restores, bulk upserts, remote-link
 * identity changes). `server_acked_mut` / `google_acked_mut` record the `mut` a
 * successful upload (or an inbound apply whose content by construction equals
 * that destination) carried, clamped so an acknowledgement never runs ahead of
 * `mut` and never moves backwards.
 *
 *     dirty(destination) === mut > acked_mut(destination)
 *
 * This replaces every timestamp-vs-watermark cursor the engines used to infer
 * dirtiness from (`updated_at > lastSyncAt`, `effectiveUpdatedAt > last_push_at`,
 * the pull's `touched` skip set): those all broke the same way — a fact about ONE
 * row was being reconstructed from a comparison between two clocks that nothing
 * keeps in step. The counters are meaningful only on the device that wrote them:
 * they are never transmitted, never adopted from the wire, and never compared
 * across devices. `updated_at`/`rev` remain the cross-device LWW ordering keys.
 */
export interface DeviceMutState {
  /** Local write counter for this row. Absent (pre-migration / wire copies) reads as 0. */
  mut?: number;
  /** The `mut` the sync server has acknowledged. DEVICE-LOCAL. */
  server_acked_mut?: number;
  /** The `mut` a Google reconcile has acknowledged. DEVICE-LOCAL. */
  google_acked_mut?: number;
}

/** Project a row for transmission/backup: drop the device-local mut counters.
 *  (Tasks additionally drop the `etag` — see `stripDeviceLocalLink`.) */
export function stripDeviceMutState<T extends DeviceMutState>(row: T): T {
  if (
    row.mut === undefined &&
    row.server_acked_mut === undefined &&
    row.google_acked_mut === undefined
  ) {
    return row;
  }
  const { mut: _mut, server_acked_mut: _server, google_acked_mut: _google, ...rest } = row;
  return rest as T;
}

/** True when this row has local writes destination `ackedMut` has not covered. */
export function isMutDirty(row: DeviceMutState, ackedField: "server_acked_mut" | "google_acked_mut"): boolean {
  return (row.mut ?? 0) > (row[ackedField] ?? 0);
}
